import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook';
import { useUserContext } from 'src/providers/UserProvider/User.context';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { WorkspaceService } from 'src/providers/WorkspaceProvider/Workspace.service';

/**
 * Cross-instance single-flight guard. Two surfaces mount this hook at once
 * whenever the user is inactive (sidebar row + InactiveUserBanner), and each
 * instance's `isUpdating` state can't see the other's in-flight request —
 * without this flag, clicking both surfaces fires concurrent request pairs
 * against the same endpoint. Module-scoped is enough: the hook only ever
 * mutates the signed-in user's own flag.
 */
let availabilityUpdateInFlight = false;

export type tUserAvailability = {
  /**
   * Provider truth: false only when the workspace marks the current user
   * inactive. Meaningless until `isLoaded` is true — gate UI on it.
   */
  isActive: boolean;
  /** True once `workspaceInfo.users` contains the current user's entry. */
  isLoaded: boolean;
  /** True while this surface's availability update is in flight. */
  isUpdating: boolean;
  /** Persist the new state and sync it back into the WorkspaceProvider. */
  setAvailability: (nextIsActive: boolean) => Promise<void>;
};

/**
 * Availability (Activo / Inactivo) of the signed-in user, derived from
 * `workspaceInfo` instead of a component-local mirror.
 *
 * RET-1350: the sidebar toggle used to keep its own `useState(true)` default
 * and sync from the provider in an effect, so a click landing before the
 * sync sent the wrong direction (observed in prod as true,true → false,false
 * double-toggles). Deriving here removes that window entirely; consumers
 * disable their controls until `isLoaded`.
 *
 * On success the fresh flag is pushed through `updateWorkspaceUser`, so every
 * surface that renders availability (avatar pill, inactive banner, Dashboard
 * advisor cards) re-renders from the same source of truth. The merge uses
 * the LATEST provider entry (via ref) rather than the click-time snapshot,
 * so a socket update landing during the request (e.g. the owner editing this
 * user's role) is not clobbered by the write-back.
 */
export function useUserAvailability(): tUserAvailability {
  const { t } = useTranslation('common/userAvailability');
  const { addAlert } = useToastContext();
  const { currentUserId } = useUserContext();
  const { currentWorkspaceId, workspaceInfo, updateWorkspaceUser } =
    useWorkspaceContext();
  const [isUpdating, setIsUpdating] = useState(false);

  const currentUserInfo = currentUserId
    ? workspaceInfo?.users?.[currentUserId]
    : undefined;
  const isLoaded = currentUserInfo !== undefined;
  const isActive = !currentUserInfo?.isInactive;

  // Always points at the latest rendered provider entry, so the post-await
  // merge below doesn't write a stale snapshot back into the provider.
  const currentUserInfoRef = useRef(currentUserInfo);
  currentUserInfoRef.current = currentUserInfo;

  async function setAvailability(nextIsActive: boolean): Promise<void> {
    // Consumers keep their controls disabled until `isLoaded`, so this guard
    // only trips on programmer error — not on a user-visible dead button.
    if (!currentWorkspaceId || !currentUserId || !currentUserInfo) return;
    if (isUpdating || availabilityUpdateInFlight) return;

    const requestedInactive = !nextIsActive;

    availabilityUpdateInFlight = true;
    setIsUpdating(true);
    try {
      const response = await WorkspaceService.updateIsInactiveUserWorkspace({
        workspaceId: currentWorkspaceId,
        userId: currentUserId,
        isInactive: requestedInactive,
      });
      const isInactive = response.data?.is_inactive ?? requestedInactive;
      const latestUserInfo = currentUserInfoRef.current ?? currentUserInfo;
      updateWorkspaceUser({ ...latestUserInfo, isInactive });
    } catch (error) {
      const normalizedError =
        error instanceof Error ? error : new Error(String(error));
      LLog.error(
        {
          component: LOG_COMPONENTS.API,
          method: 'setAvailability',
          operation: LOG_OPERATIONS.API_CALL,
        },
        'Failed to update user availability',
        normalizedError,
        {
          workspaceId: currentWorkspaceId,
          userId: currentUserId,
          requestedInactive,
        },
      );
      addAlert({ type: eAlertType.ERROR, message: t('updateError') });
    } finally {
      availabilityUpdateInFlight = false;
      setIsUpdating(false);
    }
  }

  return { isActive, isLoaded, isUpdating, setAvailability };
}
